import { rootMargin } from '@helpers/animate';
import { InterfaceIcon, getSvgIcon } from '@helpers/icons';
import { imageUrl } from '@helpers/image';
import { clamp } from '@helpers/utils';
import { useInView } from '@hooks/useInView';
import { useOutsideClick } from '@hooks/useOutsideClick';
import { LinkButton } from '@stories/Components/Buttons/Button/Button';
import HeadingSummaryBlock from '@stories/Components/Misc/HeadingSummaryBlock/HeadingSummaryBlock';
import * as React from 'react';
import S from './LogoList.styles';
const THRESHOLDS = [Infinity, 10, 6, 4, 3, 2, 1];
const LogoList = (props) => {
    const [containerRef, inView] = useInView({ rootMargin }, false, props.pageContext?.isEditMode);
    const tabsRef = useOutsideClick(() => setOpen(false));
    const [active, setActive] = React.useState(0);
    const [open, setOpen] = React.useState(false);
    const [delays, setDelays] = React.useState(true);
    const tabRef = React.useRef([]);
    React.useEffect(() => {
        if (inView) {
            setTimeout(() => setDelays(false), 1000);
        }
    }, [inView]);
    if (!props.groups || props.groups.length === 0) {
        return null;
    }
    return (React.createElement(S.Container, { id: props.anchorId, ref: containerRef, "data-animate": inView, translations: props.translations, "data-delays": delays },
        React.createElement(HeadingSummaryBlock, { ...props, summary: props.summary ? `<p>${props.summary}</p>` : undefined, layout: "central" }),
        React.createElement(S.TabWrapper, { "data-open": open, "data-hidden": props.groups.length === 1 },
            React.createElement(S.Tabs, { role: "tablist", ref: tabsRef }, props.groups.map((group, i) => (React.createElement(React.Fragment, { key: `${group.id}-button` },
                React.createElement(S.TabButton, { id: group.id, ref: (el) => (tabRef.current[i] = el), role: "tab", "aria-selected": active === i, onClick: () => {
                        if (active !== i) {
                            setActive(i);
                        }
                        else if (props.groups.length > 2) {
                            setOpen(!open);
                        }
                    }, onFocus: () => setActive(i), tabIndex: props.groups.length === 1 || active !== i ? -1 : undefined, onKeyDown: (event) => {
                        const nextIndex = i === props.groups.length - 1 ? 0 : clamp(i + 1, props.groups.length - 1);
                        const prevIndex = i === 0 ? props.groups.length - 1 : clamp(i - 1, props.groups.length - 1);
                        switch (event.key) {
                            case 'ArrowRight':
                            case 'ArrowDown':
                                event.preventDefault();
                                event.stopPropagation();
                                tabRef.current[nextIndex]?.focus();
                                break;
                            case 'ArrowLeft':
                            case 'ArrowUp':
                                event.preventDefault();
                                event.stopPropagation();
                                tabRef.current[prevIndex]?.focus();
                                break;
                            case 'Home':
                                tabRef.current[0]?.focus();
                                break;
                            case 'End':
                                tabRef.current[props.groups.length - 1]?.focus();
                                break;
                            default:
                                break;
                        }
                    } },
                    group.title,
                    getSvgIcon(InterfaceIcon.Chevron)),
                props.groups.length === 2 && i === 0 && React.createElement(S.Divider, { role: "none" })))))),
        props.groups.map((group, i) => (React.createElement(S.LogosWrapper, { key: `${group.id}-wrapper`, role: "tabpanel", "aria-labelledby": group.id, "data-active": active === i },
            React.createElement(S.LogosTrack, { style: {
                    '--speed': `${group.items.length * 2}s`,
                    '--count': group.items.length,
                } },
                group.items.map((item, j) => (React.createElement(S.Card, { key: `${group.id}-card-${j}` },
                    React.createElement(S.ImageWrapper, { ...(item.link && item.link.url
                            ? {
                                as: 'a',
                                href: item.link.url,
                                'aria-label': item.link.text ?? item.label,
                                target: '_blank',
                                rel: 'nofollow',
                            }
                            : {}) },
                        React.createElement(S.Image, { src: imageUrl(item, { height: 128, width: 416, rmode: 'pad' }), alt: item.altText ?? item.label, loading: "lazy" })),
                    item.label && React.createElement("span", null, item.label)))),
                THRESHOLDS.map((threshold, j) => {
                    if (group.items.length > threshold) {
                        return null;
                    }
                    return (React.createElement(React.Fragment, { key: `${group.id}-card-${j + 1}` }, group.items.map((item, k) => (React.createElement(S.Card, { key: `${group.id}-card-${j}-${k}` },
                        React.createElement(S.ImageWrapper, { ...(item.link && item.link.url
                                ? {
                                    as: 'a',
                                    href: item.link.url,
                                    'aria-label': item.link.text ?? item.label,
                                    target: '_blank',
                                    rel: 'nofollow',
                                }
                                : {}) },
                            React.createElement(S.Image, { src: imageUrl(item, { height: 128, width: 416, rmode: 'pad' }), alt: item.altText ?? item.label, loading: "lazy" })),
                        item.label && React.createElement("span", null, item.label))))));
                })),
            group.ctas && group.ctas.length > 0 ? (React.createElement(S.ButtonWrapper, { "data-active": active === i }, group.ctas.map((cta, i) => (React.createElement(LinkButton, { key: `${group.id}-cta-${i}`, buttonType: cta.buttonType ? cta.buttonType : i === 0 ? 'secondary' : 'tertiary', href: cta.url, target: cta.target, icon: cta.icon }, cta.text))))) : null)))));
};
export default LogoList;
